import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getDeviceId } from '@/lib/device';

/**
 * Debug page — afișează în consolă și pe pagină:
 *  - status conectare Supabase
 *  - UUID-ul din localStorage
 *  - env vars VITE_* disponibile la build time (și demonstrează că b.token encrypted NU e accesibil din browser)
 *  - încearcă să citească b.token prin env vars
 */
export default function DebugPage() {
  const [report, setReport] = useState<Record<string, any>>({});

  useEffect(() => {
    const deviceId = getDeviceId();
    const envSnapshot = {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ?? '(undefined)',
      VITE_SUPABASE_ANON_KEY: ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.slice(0, 20) ?? '(undefined)') + '…',
      VITE_B_TOKEN: import.meta.env.VITE_B_TOKEN ? '***' + ((import.meta.env.VITE_B_TOKEN as string).slice(-6)) : '(undefined — tokenul trebuie setat în Cloudflare ca VITE_B_TOKEN)',
      VITE_TELEGRAM_CHAT_ID: import.meta.env.VITE_TELEGRAM_CHAT_ID ?? '(undefined)',
      VITE_TELEGRAM_THREAD_ID: import.meta.env.VITE_TELEGRAM_THREAD_ID ?? '(undefined)',
      VITE_TELEGRAM_CHANNEL_NAME: import.meta.env.VITE_TELEGRAM_CHANNEL_NAME ?? '(undefined)',
    };

    // eslint-disable-next-line no-console
    console.log('[DEBUG] ===== GuestLens debug report =====');
    // eslint-disable-next-line no-console
    console.log('[DEBUG] deviceId (localStorage):', deviceId);
    // eslint-disable-next-line no-console
    console.log('[DEBUG] env vars:', envSnapshot);
    // eslint-disable-next-line no-console
    console.log('[DEBUG] all import.meta.env keys:', Object.keys(import.meta.env));

    // Test Supabase connection
    (async () => {
      const t0 = performance.now();
      try {
        const { error, count } = await supabase
          .from('photos')
          .select('id', { count: 'exact', head: true });
        const dt = Math.round(performance.now() - t0);
        const supabaseStatus = error
          ? { ok: false, error: error.message, latency_ms: dt }
          : { ok: true, count, latency_ms: dt };
        // eslint-disable-next-line no-console
        console.log('[DEBUG] supabase.photos ping:', supabaseStatus);
        setReport((r) => ({ ...r, supabase: supabaseStatus }));
      } catch (e) {
        const err = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error('[DEBUG] supabase ping FAILED:', err);
        setReport((r) => ({ ...r, supabase: { ok: false, error: err } }));
      }
    })();

    // Test Telegram direct (will FAIL if no VITE_B_TOKEN)
    (async () => {
      const token = import.meta.env.VITE_B_TOKEN as string | undefined;
      if (!token) {
        // eslint-disable-next-line no-console
        console.warn('[DEBUG] Telegram direct: SKIP (no VITE_B_TOKEN in env)');
        setReport((r) => ({ ...r, telegram: { ok: false, error: 'No token (VITE_B_TOKEN undefined)' } }));
        return;
      }
      try {
        const resp = await fetch(`https://api.telegram.org/bot${token}/getMe`);
        const json = await resp.json();
        // eslint-disable-next-line no-console
        console.log('[DEBUG] Telegram getMe:', json);
        setReport((r) => ({ ...r, telegram: { ok: json.ok, bot: json.result?.username, error: json.description } }));
      } catch (e) {
        const err = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error('[DEBUG] Telegram getMe FAILED:', err);
        setReport((r) => ({ ...r, telegram: { ok: false, error: err } }));
      }
    })();

    setReport((r) => ({ ...r, deviceId, env: envSnapshot, hostname: window.location.hostname }));
  }, []);

  return (
    <div className="min-h-screen bg-bg p-xl font-mono text-sm">
      <h1 className="text-2xl font-serif">Debug — GuestLens / larisa-si-dani</h1>
      <p className="mt-md text-fg-soft">
        Deschide <strong>DevTools → Console</strong> pentru log-uri detaliate.
      </p>

      <section className="mt-xl">
        <h2 className="text-lg">Device UUID (localStorage)</h2>
        <pre className="mt-sm rounded-md bg-bg-alt p-md text-xs">
{report.deviceId ?? '…'}
        </pre>
        <p className="mt-xs text-xs text-fg-muted">
          Acest UUID persistă prin schimbări de rețea (WiFi → date mobile). Se șterge doar dacă userul golește cache-ul.
        </p>
      </section>

      <section className="mt-xl">
        <h2 className="text-lg">Environment vars (VITE_*)</h2>
        <p className="mt-xs text-xs text-fg-muted">
          Acestea sunt citite la <strong>build time</strong>. Cloudflare NU trimite encrypted secrets (cum e <code>b.token</code>) la build — din motive de securitate.
        </p>
        <pre className="mt-sm overflow-x-auto rounded-md bg-bg-alt p-md text-xs">
{JSON.stringify(report.env ?? {}, null, 2)}
        </pre>
      </section>

      <section className="mt-xl">
        <h2 className="text-lg">Supabase ping</h2>
        <pre className="mt-sm rounded-md bg-bg-alt p-md text-xs">
{JSON.stringify(report.supabase ?? { status: 'loading…' }, null, 2)}
        </pre>
      </section>

      <section className="mt-xl">
        <h2 className="text-lg">Telegram Bot API test</h2>
        <pre className="mt-sm rounded-md bg-bg-alt p-md text-xs">
{JSON.stringify(report.telegram ?? { status: 'loading…' }, null, 2)}
        </pre>
      </section>

      <section className="mt-xl">
        <h2 className="text-lg">Concluzie</h2>
        <ul className="mt-sm list-disc pl-lg text-sm">
          <li><strong>Supabase</strong> — conectat (vede count).</li>
          <li><strong>Telegram direct din browser</strong> — funcționează DOAR dacă tokenul e VITE_TELEGRAM_BOT_TOKEN (plain). Dacă e encrypted b.token, browser-ul nu-l poate citi.</li>
          <li><strong>UUID</strong> — generat o singură dată per browser, persistă la nivel de localStorage.</li>
        </ul>
      </section>
    </div>
  );
}
